<template>
  <vs-card>
    <form action="/charge" method="post" id="payment-form">
      <div class="form-row">
        <label for="card-element">
          Credit or debit card
        </label>
        <div ref="card"><!-- A Stripe Element will be inserted here. --></div>
        <!-- Used to display form errors. -->
        <div id="card-errors" role="alert"></div>
      </div>
    </form>
    <div v-if="dashboardView"  class="vx-col md:w-1/2 mt-5" >
      <vs-button
        @click="addPaymentCard()"
        color="primary"
        type="filled"
        class="customizer-btn">Add Payment Card</vs-button>
    </div>
  </vs-card>
</template>
<script>
import { Validator } from 'vee-validate'
import { 
  getStripePaymentMethod,
  attachPaymentMethodToStripeCustomer 
} from '../../../stripe/stripeHelperMethods'

const stripe = (process.env.NODE_ENV === 'production') ? 
  window.Stripe('pk_live_kcXgNrDwoI3iiiHuvi9a97RR00qDqMCRpz') : 
  window.Stripe('pk_test_T6ViIWIa5d9ikgwAKHiYIt4T00frFt4uMg')

const elements = stripe.elements();
const cardFormStyle = {
  base: {
    color: '#32325d',
    fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
    fontSmoothing: 'antialiased',
    fontSize: '16px',
    '::placeholder': {
      color: '#aab7c4'
    }
  },
  invalid: {
    color: '#fa755a',
    iconColor: '#fa755a'
  }
};

const dict = {
  custom: {
    card_number: {
      required: 'Card number is required',
      numeric: 'Card number may only contain numbers',
      max: 'Card number may not be greater than 19 digits'
    },
    card_expiration_month: {
      required: 'Card expiration month is required',
      numeric: 'Card expiration month may only contain numbers',
      max: 'Card expiration month may not be greater than 2 digits'
    },
    card_expiration_year: {
      required: 'Card expiration year is required',
      numeric: 'Card expiration year may only contain numbers',
      max: 'Card expiration year may not be greater than 2 digits'
    },
    card_cvc: {
      required: 'Card CVC code is required',
      numeric: 'Card CVC code may only contain numbers',
      max: 'Card CVC code may not be greater than 4 digits'
    },
  }
}
// register custom messages
Validator.localize('en', dict)

export default {
  props: {
      dashboardView: Boolean
  },
  data () {
      return {
          card: {},
          stripeCustomer: {},
          stripeCards: {}
      }
  },
  mounted() {
      this.stripeCards = this.$store.state.AppActiveUser.stripeCards
      this.stripeCustomer = this.$store.state.AppActiveUser.stripeCustomer
      this.loadCardForm()
  },
  beforeDestroy() {
    this.card.destroy(this.$refs.card);
  },
  watch: {
      cardNumber() {
          this.$emit('card-number-update', this.cardNumber)
      },
      cardExpirationMonth() {
          this.$emit('card-exp-month-update', this.cardExpirationMonth)
      },
      cardExpirationYear() {
          this.$emit('card-exp-year-update', this.cardExpirationYear)
      },
      cardCvc() {
          this.$emit('card-cvc-update', this.cardCvc)
      }
  },
  methods: {
    loadCardForm() {
      // Create an instance of the card Element.
      this.card = elements.create('card', { style: cardFormStyle });
      this.card.mount(this.$refs.card);

      // Handle real-time validation errors from the card Element.
      this.card.addEventListener('change', (event) => {
        let displayError = document.getElementById('card-errors')
        if (event.error) {
          displayError.textContent = event.error.message
        } else {
          displayError.textContent = ''
        }
      });
    },
    async addPaymentCard() {
      this.$vs.loading()

      this.stripeCustomer = this.$store.state.AppActiveUser.stripeCustomer.data || this.$store.state.AppActiveUser.stripeCustomer

      const createPaymentMethodProps = {
        name: this.stripeCustomer.name,
        email: this.stripeCustomer.email,
        phone: this.stripeCustomer.phone,
        address: {
          line1: this.stripeCustomer.address.line1,
          city: this.stripeCustomer.address.city,
          postal_code: this.stripeCustomer.address.postal_code,
          state: this.stripeCustomer.address.state,
          country: this.stripeCustomer.address.country
        }
      }
          
      return await stripe.createPaymentMethod({
          type: 'card',
          card: this.card,
          billing_details: createPaymentMethodProps,
        })
        .then(async (result) => {
          if (result.error) {
            throw result.error
          }
          
          let canAttachCard = await this.canAttachCard(result.paymentMethod.id)
          const attachPaymentMethodProps = {
            paymentMethodId: result.paymentMethod.id,
            customer: this.$store.state.AppActiveUser.stripe_id
          }

          if (canAttachCard) {
            return await attachPaymentMethodToStripeCustomer(attachPaymentMethodProps)
              .then (result => {
                //console.log('Attach payment POST result:',result);
                this.$store.commit('ADD_CARD', result.data, { root: true }) 
                this.$store.commit('UPDATE_USER_INFO', { stripePaymentMethod: result.data }, { root: true })
                this.stripeCards = this.$store.state.AppActiveUser.stripeCards
                this.$emit('card-added', this.stripeCards)
                
                if (this.dashboardView === true) {
                  this.$vs.notify({
                    time: 4000,
                    title: 'Add Payment Card',
                    text: 'Payment Card added successfully',
                    iconPack: 'feather',
                    icon: 'icon-alert-circle',
                    color: 'success'
                  })
                }

                return { result: 'resolve', payload: true }
              }).catch(err => {
                const errMsg = 'An error occured while adding a payment card: ' + err.response.data.raw.message;
                if (this.dashboardView === true) {
                  this.$vs.notify({
                    time: 10000,
                    title: 'Add Payment Card',
                    text: errMsg,
                    iconPack: 'feather',
                    icon: 'icon-alert-circle',
                    color: 'danger'
                  })
                }
                return { result: 'reject', payload: 'Could not attach the payment card to customer: ' + errMsg }
              }).finally(() => {
                this.$vs.loading.close()
              })
          } else { // Cannot add card
              this.$vs.loading.close()

              if (this.dashboardView === true) {
                this.$vs.notify({
                    time: 10000,
                    title: 'Add Payment Card',
                    text: 'The payment card you tried to add is already found.',
                    iconPack: 'feather',
                    icon: 'icon-alert-circle',
                    color: 'warning'
                })
              }
              
              return { result: 'resolve', payload: true }
          }
        })
        .catch(err => {
          const errMsg = 'An error occured while adding a payment card: ' + err.message;
          if (this.dashboardView === true) {
            this.$vs.notify({
              time: 10000,
              title: 'Add Payment Card',
              text: errMsg,
              iconPack: 'feather',
              icon: 'icon-alert-circle',
              color: 'danger'
            })
          }

          return { result: 'reject', payload: errMsg }
        })
        .finally(() => {
          this.$vs.loading.close()
        });
    },
    async canAttachCard(paymentMethodId) {
      /** 
       * Only attach card which is not already found in state.AppActiveUser.stripeCards
       * 
       * fingerprint is not found on stripe.createPaymentMethod() response object: result.paymentMethod.card
       * We need to fetch the freshly created payment method to get the fingerprint
      */
      let newPaymentMethod = {}
      return await getStripePaymentMethod(paymentMethodId).then(result => {
        newPaymentMethod = result.data
        if (typeof this.stripeCards.data === 'undefined') {
          return true
        } else {
          return(this.stripeCards.data.filter(obj => obj.card.fingerprint === newPaymentMethod.card.fingerprint).length === 0)
        }
      }).catch(() => {
          return false
      });
    }
  }
}
</script>
<style scoped>
.expiration-delimiter {
  position: relative;
}
.expiration-delimiter>div {
  position: absolute;
  bottom: 7px;
  margin-left: -3px;
}
.vx-col.centered {
  text-align: center;
}
.custom-label.expiration {
    margin-left: 5px;
    margin-bottom: -15px;
    text-align: center;
}
.card-icons {
  margin: 20px;
  display: flex;
}
.card-icons img {
  height: 60px;
}
</style>